/**
 * Custom Report Builder Component
 * Allows users to build custom reports with selected fields and filters
 */

import { useState, useEffect } from 'react'
import { useOrganizationId } from '../hooks/useOrganizationId'
import Card from './Card'
import Button from './Button'
import Input from './Input'
import Select from './Select'
import {
  FileText,
  Plus,
  Trash2,
  Play,
  Save,
  Download,
  Filter,
  X,
} from 'lucide-react'
import {
  REPORT_SOURCES,
  getAvailableFields,
  buildReport,
  saveReport,
  getSavedReports,
  deleteReport,
} from '../services/reportBuilder'
import { exportReport } from '../services/exportService'

const ReportBuilder = () => {
  const organizationId = useOrganizationId()
  const [source, setSource] = useState(REPORT_SOURCES.CLIENTS)
  const [selectedFields, setSelectedFields] = useState([])
  const [filters, setFilters] = useState([])
  const [sortBy, setSortBy] = useState({ field: '', ascending: true })
  const [limit, setLimit] = useState(100)
  const [reportName, setReportName] = useState('')
  const [reportData, setReportData] = useState(null)
  const [savedReports, setSavedReports] = useState([])
  const [loading, setLoading] = useState(false)
  const [showSavedReports, setShowSavedReports] = useState(false)

  useEffect(() => {
    if (organizationId) {
      loadSavedReports()
    }
  }, [organizationId])

  useEffect(() => {
    // Reset selected fields when source changes
    setSelectedFields([])
    setFilters([])
  }, [source])

  const loadSavedReports = async () => {
    const result = await getSavedReports(organizationId)
    if (result.success) {
      setSavedReports(result.data)
    }
  }

  const availableFields = getAvailableFields(source)

  const handleFieldToggle = (field) => {
    if (selectedFields.includes(field.name)) {
      setSelectedFields(selectedFields.filter((f) => f !== field.name))
    } else {
      setSelectedFields([...selectedFields, field.name])
    }
  }

  const handleAddFilter = () => {
    setFilters([
      ...filters,
      {
        field: '',
        operator: 'equals',
        value: '',
      },
    ])
  }

  const handleFilterChange = (index, key, value) => {
    const newFilters = [...filters]
    newFilters[index][key] = value
    setFilters(newFilters)
  }

  const handleRemoveFilter = (index) => {
    setFilters(filters.filter((_, i) => i !== index))
  }

  const handleRunReport = async () => {
    if (selectedFields.length === 0) {
      alert('Please select at least one field')
      return
    }

    setLoading(true)
    try {
      const result = await buildReport(
        {
          source,
          fields: selectedFields,
          filters: filters.filter((f) => f.field && f.value),
          sortBy: sortBy.field ? sortBy : null,
          limit,
        },
        organizationId
      )

      if (result.success) {
        setReportData({
          ...result,
          name: reportName || `${source} Report`,
          fields: selectedFields,
        })
      } else {
        alert(`Error: ${result.error}`)
      }
    } catch (error) {
      alert(`Error running report: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleSaveReport = async () => {
    if (!reportName) {
      alert('Please enter a report name')
      return
    }

    const { supabase } = await import('../services/supabase')
    const {
      data: { user },
    } = await supabase.auth.getUser()

    const result = await saveReport(
      {
        name: reportName,
        source,
        fields: selectedFields,
        filters: filters.filter((f) => f.field && f.value),
        sortBy: sortBy.field ? sortBy : null,
        limit,
      },
      organizationId,
      user?.id
    )

    if (result.success) {
      alert('Report saved successfully')
      loadSavedReports()
    } else {
      alert(`Error saving report: ${result.error}`)
    }
  }

  const handleLoadReport = (report) => {
    const config = report.report_config
    setSource(config.source)
    setSelectedFields(config.fields || [])
    setFilters(config.filters || [])
    setSortBy(config.sortBy || { field: '', ascending: true })
    setLimit(config.limit || 100)
    setReportName(report.report_name)
    setShowSavedReports(false)
  }

  const handleDeleteReport = async (reportId) => {
    if (!confirm('Are you sure you want to delete this report?')) return

    const result = await deleteReport(reportId, organizationId)
    if (result.success) {
      loadSavedReports()
    } else {
      alert(`Error deleting report: ${result.error}`)
    }
  }

  const handleExport = async (format) => {
    if (!reportData) return
    await exportReport(reportData, format)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <FileText className="w-8 h-8" />
          Custom Report Builder
        </h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowSavedReports(!showSavedReports)}>
            {showSavedReports ? 'Hide' : 'Show'} Saved Reports
          </Button>
        </div>
      </div>

      {/* Saved Reports */}
      {showSavedReports && (
        <Card>
          <h2 className="text-xl font-semibold mb-4">Saved Reports</h2>
          {savedReports.length === 0 ? (
            <p className="text-gray-500">No saved reports</p>
          ) : (
            <div className="space-y-2">
              {savedReports.map((report) => (
                <div
                  key={report.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded"
                >
                  <div>
                    <p className="font-medium">{report.report_name}</p>
                    <p className="text-sm text-gray-600">
                      {report.report_config.source} • {report.run_count || 0} runs
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleLoadReport(report)}>
                      Load
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteReport(report.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Configuration Panel */}
        <div className="lg:col-span-1 space-y-6">
          <Card>
            <h2 className="text-xl font-semibold mb-4">Report Configuration</h2>

            {/* Data Source */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Data Source</label>
              <Select
                value={source}
                onChange={(e) => setSource(e.target.value)}
                options={Object.values(REPORT_SOURCES).map((s) => ({
                  value: s,
                  label: s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' '),
                }))}
              />
            </div>

            {/* Report Name */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Report Name</label>
              <Input
                value={reportName}
                onChange={(e) => setReportName(e.target.value)}
                placeholder="Enter report name"
              />
            </div>

            {/* Fields Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Select Fields</label>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {availableFields.map((field) => (
                  <label key={field.name} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedFields.includes(field.name)}
                      onChange={() => handleFieldToggle(field)}
                      className="rounded"
                    />
                    <span className="text-sm">{field.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Filters */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium">Filters</label>
                <Button size="sm" variant="ghost" onClick={handleAddFilter}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>
              <div className="space-y-2">
                {filters.map((filter, index) => (
                  <div key={index} className="flex gap-2 items-start">
                    <Select
                      value={filter.field}
                      onChange={(e) => handleFilterChange(index, 'field', e.target.value)}
                      options={availableFields.map((f) => ({
                        value: f.name,
                        label: f.label,
                      }))}
                      placeholder="Field"
                      className="flex-1"
                    />
                    <Select
                      value={filter.operator}
                      onChange={(e) => handleFilterChange(index, 'operator', e.target.value)}
                      options={[
                        { value: 'equals', label: '=' },
                        { value: 'not_equals', label: '≠' },
                        { value: 'contains', label: 'Contains' },
                        { value: 'greater_than', label: '>' },
                        { value: 'less_than', label: '<' },
                      ]}
                      className="w-32"
                    />
                    <Input
                      value={filter.value}
                      onChange={(e) => handleFilterChange(index, 'value', e.target.value)}
                      placeholder="Value"
                      className="flex-1"
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRemoveFilter(index)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Sort By */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Sort By</label>
              <div className="flex gap-2">
                <Select
                  value={sortBy.field}
                  onChange={(e) => setSortBy({ ...sortBy, field: e.target.value })}
                  options={availableFields.map((f) => ({
                    value: f.name,
                    label: f.label,
                  }))}
                  placeholder="Field"
                  className="flex-1"
                />
                <Select
                  value={sortBy.ascending ? 'asc' : 'desc'}
                  onChange={(e) => setSortBy({ ...sortBy, ascending: e.target.value === 'asc' })}
                  options={[
                    { value: 'asc', label: 'Asc' },
                    { value: 'desc', label: 'Desc' },
                  ]}
                  className="w-24"
                />
              </div>
            </div>

            {/* Limit */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Limit</label>
              <Input
                type="number"
                value={limit}
                onChange={(e) => setLimit(parseInt(e.target.value) || 100)}
                min={1}
                max={10000}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <Button onClick={handleRunReport} disabled={loading} className="flex-1">
                <Play className="w-4 h-4 mr-2" />
                Run Report
              </Button>
              <Button variant="outline" onClick={handleSaveReport}>
                <Save className="w-4 h-4" />
              </Button>
            </div>
          </Card>
        </div>

        {/* Results Panel */}
        <div className="lg:col-span-2">
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Report Results</h2>
              {reportData && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => handleExport('csv')}>
                    <Download className="w-4 h-4 mr-1" />
                    CSV
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleExport('xlsx')}>
                    <Download className="w-4 h-4 mr-1" />
                    Excel
                  </Button>
                </div>
              )}
            </div>

            {loading ? (
              <div className="text-center py-12">
                <p className="text-gray-600">Running report...</p>
              </div>
            ) : reportData ? (
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  {reportData.count} records found
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        {reportData.fields.map((field) => {
                          const fieldInfo = availableFields.find((f) => f.name === field)
                          return (
                            <th key={field} className="text-left p-2 font-semibold">
                              {fieldInfo?.label || field}
                            </th>
                          )
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.data.slice(0, 100).map((row, index) => (
                        <tr key={index} className="border-b">
                          {reportData.fields.map((field) => (
                            <td key={field} className="p-2">
                              {row[field] !== null && row[field] !== undefined
                                ? String(row[field])
                                : ''}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {reportData.data.length > 100 && (
                    <p className="text-sm text-gray-500 mt-2">
                      Showing first 100 of {reportData.data.length} records
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500">Run a report to see results</p>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  )
}

export default ReportBuilder

